import { del, get, patch, post, put } from '@/lib/api';
import { num, rec, str, strArray, strOrNull, unwrapList } from './normalize';
import type {
  Activity,
  BillableParty,
  Client,
  CreditFeeTemplate,
  Paginated,
  TeamUser
} from '@/types';

// ------------------------------------------------------------------ clients

function toClient(v: unknown): Client {
  const c = rec(v);
  const b = c.billable ? rec(c.billable) : null;
  const status = c.status === 'inactive' || c.status === 'archived' ? c.status : 'active';
  return {
    id: str(c.id),
    name: str(c.name),
    status,
    billable: b
      ? {
          id: str(b.id),
          name: str(b.name),
          address: strOrNull(b.address),
          phone: strOrNull(b.phone),
          emails: strArray(b.emails ?? b.email)
        }
      : null,
    hourlyRate:
      c.hourlyRate === null || c.hourlyRate === undefined || c.hourlyRate === ''
        ? null
        : num(c.hourlyRate),
    totalHours: c.totalHours !== undefined ? num(c.totalHours) : undefined
  };
}

export async function fetchClients(signal?: AbortSignal): Promise<Client[]> {
  const raw = await get<unknown>('/api/clients', { all: true }, signal);
  return unwrapList(raw, 'clients').map(toClient);
}

export async function fetchClientsPage(
  params: { page?: number; pageSize?: number; search?: string },
  signal?: AbortSignal
): Promise<Paginated<Client>> {
  const raw = rec(
    await get<unknown>(
      '/api/clients',
      { page: params.page ?? 1, pageSize: params.pageSize ?? 50, search: params.search },
      signal
    )
  );
  return {
    items: unwrapList(raw, 'clients').map(toClient),
    page: num(raw.page, 1),
    pageSize: num(raw.pageSize, 50),
    totalCount: num(raw.totalCount),
    totalPages: num(raw.totalPages, 1)
  };
}

export const createClient = (input: {
  name: string;
  status?: Client['status'];
  hourlyRate?: number | null;
}) =>
  post<unknown>('/api/clients', {
    name: input.name,
    status: input.status ?? 'active',
    hourlyRate: input.hourlyRate ?? null
  }).then(toClient);

export const updateClient = (
  id: string,
  input: Partial<{ name: string; status: string; hourlyRate: number | null }>
) =>
  patch<unknown>(`/api/clients/${id}`, input).then(toClient);

export const deleteClient = (id: string) => del(`/api/clients/${id}`);

export const mergeClients = (input: {
  clientIds: string[];
  targetClientId: string | null;
  newName: string | null;
}) => post('/api/clients/merge', input);

export const setClientBillable = (clientId: string, billableId: string | null) =>
  billableId
    ? post(`/api/clients/${clientId}/billable-relationship`, { billableId, isDefault: true })
    : del(`/api/clients/${clientId}/billable-relationship`);

// --------------------------------------------------------------- activities

function toActivity(v: unknown): Activity {
  const a = rec(v);
  return { id: str(a.id), name: str(a.name) };
}

export async function fetchActivities(signal?: AbortSignal): Promise<Activity[]> {
  const raw = await get<unknown>('/api/activities', { all: true }, signal);
  return unwrapList(raw, 'activities').map(toActivity);
}

export async function fetchActivitiesPage(
  params: { page?: number; pageSize?: number; search?: string },
  signal?: AbortSignal
): Promise<Paginated<Activity>> {
  const raw = rec(
    await get<unknown>(
      '/api/activities',
      { page: params.page ?? 1, pageSize: params.pageSize ?? 50, search: params.search },
      signal
    )
  );
  return {
    items: unwrapList(raw, 'activities').map(toActivity),
    page: num(raw.page, 1),
    pageSize: num(raw.pageSize, 50),
    totalCount: num(raw.totalCount),
    totalPages: num(raw.totalPages, 1)
  };
}

export const createActivity = (name: string) =>
  post<unknown>('/api/activities', { name }).then(toActivity);

export const updateActivity = (id: string, name: string) =>
  patch<unknown>(`/api/activities/${id}`, { name }).then(toActivity);

export const deleteActivity = (id: string) => del(`/api/activities/${id}`);

export const mergeActivities = (input: {
  activityIds: string[];
  targetActivityId: string | null;
  newName: string | null;
}) => post('/api/activities/merge', input);

export interface ActivitySet {
  key: string;
  label: string;
  names: string[];
}

export interface ActivitySetsResponse {
  sets: ActivitySet[];
  suggested: string;
}

export async function fetchActivitySets(signal?: AbortSignal): Promise<ActivitySetsResponse> {
  const raw = rec(await get<unknown>('/api/activities/sets', undefined, signal));
  const sets = unwrapList(raw, 'sets').map((v) => {
    const s = rec(v);
    return { key: str(s.key), label: str(s.label), names: strArray(s.names) };
  });
  return { sets, suggested: str(raw.suggested, sets[0]?.key ?? '') };
}

export interface BulkAddResult {
  created: Activity[];
  skipped: string[];
}

export const bulkAddActivities = (names: string[]): Promise<BulkAddResult> =>
  post<unknown>('/api/activities/bulk', { names }).then((raw) => {
    const r = rec(raw);
    return {
      created: unwrapList(r, 'created').map(toActivity),
      skipped: strArray(r.skipped)
    };
  });

// ---------------------------------------------------------------- billables

export function toBillable(v: unknown): BillableParty {
  const b = rec(v);
  return {
    id: str(b.id),
    name: str(b.name),
    title: strOrNull(b.title),
    organization: strOrNull(b.organization),
    address: strOrNull(b.address),
    phone: strOrNull(b.phone),
    emails: strArray(b.emails ?? b.email)
  };
}

export async function fetchBillables(signal?: AbortSignal): Promise<BillableParty[]> {
  const raw = await get<unknown>('/api/billables', { all: true }, signal);
  return unwrapList(raw, 'billables').map(toBillable);
}

export async function fetchBillablesPage(
  params: { page?: number; pageSize?: number; search?: string },
  signal?: AbortSignal
): Promise<Paginated<BillableParty>> {
  const raw = rec(
    await get<unknown>(
      '/api/billables',
      { page: params.page ?? 1, pageSize: params.pageSize ?? 50, search: params.search },
      signal
    )
  );
  return {
    items: unwrapList(raw, 'billables').map(toBillable),
    page: num(raw.page, 1),
    pageSize: num(raw.pageSize, 50),
    totalCount: num(raw.totalCount),
    totalPages: num(raw.totalPages, 1)
  };
}

export const fetchBillable = (id: string) =>
  get<unknown>(`/api/billables/${id}`).then((r) => toBillable(rec(r).data ?? r));

export type BillableInput = Omit<BillableParty, 'id'>;

export const createBillable = (input: BillableInput) =>
  post<unknown>('/api/billables', input).then(toBillable);

export const updateBillable = (id: string, input: Partial<BillableInput>) =>
  patch<unknown>(`/api/billables/${id}`, input).then(toBillable);

export const deleteBillable = (id: string) => del(`/api/billables/${id}`);

export const mergeBillables = (input: {
  billableIds: string[];
  targetBillableId: string | null;
  newName: string | null;
}) => post('/api/billables/merge', input);

// --------------------------------------------------------------- team users

export async function fetchTeamUsers(signal?: AbortSignal): Promise<TeamUser[]> {
  const raw = await get<unknown>('/api/team-users', undefined, signal);
  return unwrapList(raw, 'users').map((v) => {
    const u = rec(v);
    return {
      id: str(u.id),
      name: strOrNull(u.name),
      email: strOrNull(u.email),
      image: strOrNull(u.image)
    };
  });
}

// ------------------------------------------------------- credit/fee templates

function toTemplate(v: unknown): CreditFeeTemplate {
  const t = rec(v);
  const amount = num(t.amount);
  return {
    id: str(t.id),
    name: str(t.name),
    // v1 encodes credits as negative amounts on a single `amount` column.
    kind: amount < 0 ? 'credit' : 'fee',
    amount: Math.abs(amount),
    isHourly: t.isHourly === true,
    description: strOrNull(t.description)
  };
}

export async function fetchCreditFeeTemplates(
  params: { search?: string } = {},
  signal?: AbortSignal
): Promise<CreditFeeTemplate[]> {
  const raw = await get<unknown>(
    '/api/credits-and-fees-templates',
    { pageSize: 200, searchText: params.search, sortField: 'name', sortOrder: 'asc' },
    signal
  );
  return unwrapList(raw, 'templates').map(toTemplate);
}

const templateBody = (input: Omit<CreditFeeTemplate, 'id'>) => ({
  name: input.name,
  amount: input.kind === 'credit' ? -Math.abs(input.amount) : Math.abs(input.amount),
  isHourly: input.isHourly,
  description: input.description
});

export const createCreditFeeTemplate = (input: Omit<CreditFeeTemplate, 'id'>) =>
  post<unknown>('/api/credits-and-fees-templates', templateBody(input)).then(toTemplate);

export const updateCreditFeeTemplate = (id: string, input: Omit<CreditFeeTemplate, 'id'>) =>
  put<unknown>(`/api/credits-and-fees-templates/${id}`, templateBody(input)).then(toTemplate);

export const deleteCreditFeeTemplate = (id: string) =>
  del(`/api/credits-and-fees-templates/${id}`);
